import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import { ItemCalculationType } from "@/enums/document-type-enum";
import {
  applyOptimisticTaxCalculating,
  isQuickBooksPrintHouse,
} from "@/widgets/quote-new/utils/quickbooks-tax-ui";

export const isDraftInvoiceQuickBooksTax = (
  documentType: number,
  isNewCreation: boolean,
  erpType?: number,
) =>
  Boolean(isNewCreation) &&
  isQuickBooksPrintHouse(erpType) &&
  (documentType === DOCUMENT_TYPE.invoice ||
    documentType === DOCUMENT_TYPE.invoiceRefund);

export const mapDocumentItemsForDraftTax = (items: any[] = []) =>
  items.map((item) => ({
    id: item.id,
    productID: item.productID,
    code: item.code,
    quantity: item.quantity ?? 1,
    productName: item.productName,
    finalPrice: Number(item.finalPrice) || 0,
    discount: item.discount ?? 0,
  }));

const roundPrice = (value: number) => Math.round(value * 1000) / 1000;

export const resolvePendingItemFinalPrice = (
  calculationType: number,
  data: number,
  quoteItem: any,
): number => {
  const quantity = quoteItem?.quantity > 0 ? quoteItem.quantity : 1;
  const price = Number(quoteItem?.price) || 0;

  switch (calculationType) {
    case ItemCalculationType.finalPrice:
      return data;
    case ItemCalculationType.UnitPrice:
      return roundPrice(data * quantity);
    case ItemCalculationType.Amount:
      return roundPrice(data * price);
    case ItemCalculationType.Discount:
      return roundPrice(
        price * quantity - (data / 100) * (price * quantity),
      );
    case ItemCalculationType.PackagesQuantity:
      return roundPrice(
        data *
          (quoteItem?.quantityInPackage || quoteItem?.quantityPerPackage || 1) *
          price,
      );
    case ItemCalculationType.QuantityInPackage:
      return roundPrice(
        (quoteItem?.packagesQuantity || quoteItem?.packageQuantity || 1) *
          data *
          price,
      );
    default:
      return Number(quoteItem?.finalPrice) || 0;
  }
};

export const buildAllDocumentItemsForItemCalculation = (
  items: any[] = [],
  editedItemId: string,
  calculationType: number,
  data: number,
  quoteItem: any,
) => {
  const pendingFinalPrice = resolvePendingItemFinalPrice(
    calculationType,
    data,
    quoteItem,
  );
  const pendingQuantity =
    calculationType === ItemCalculationType.Amount
      ? data
      : calculationType === ItemCalculationType.PackagesQuantity
        ? data *
          (quoteItem?.quantityInPackage || quoteItem?.quantityPerPackage || 1)
        : calculationType === ItemCalculationType.QuantityInPackage
          ? (quoteItem?.packagesQuantity || quoteItem?.packageQuantity || 1) *
            data
          : quoteItem?.quantity ?? 1;
  const pendingDiscount =
    calculationType === ItemCalculationType.Discount
      ? data
      : quoteItem?.discount ?? 0;

  return mapDocumentItemsForDraftTax(items).map((item) =>
    item.id === editedItemId
      ? {
          ...item,
          finalPrice: pendingFinalPrice,
          quantity: pendingQuantity,
          discount: pendingDiscount,
        }
      : item,
  );
};

export const buildDraftTaxContext = (quoteItemValue: any) => ({
  calculateQuickBooksTax: true,
  customerId: quoteItemValue?.customerID,
  clientCode: quoteItemValue?.client?.code,
  documentBillingAddresses: quoteItemValue?.documentBillingAddresses ?? [],
  documentDeliveryAddresses: quoteItemValue?.documentDeliveryAddresses ?? [],
});

export const buildDraftCalculateDocumentPayload = (
  quoteItemValue: any,
  overrides: Record<string, any> = {},
) => ({
  exchangeRate:
    quoteItemValue?.exchangeRate === 0 ? 1 : quoteItemValue?.exchangeRate,
  totalPrice: quoteItemValue?.totalPrice,
  data:
    quoteItemValue?.discountAmount > -1 && quoteItemValue?.discountAmount < 1
      ? 0
      : quoteItemValue?.discount || 0,
  calculationType: 0,
  totalPriceAfterDiscount: quoteItemValue?.totalPriceAfterDiscount,
  discount: quoteItemValue?.discount,
  discountAmount: quoteItemValue?.discountAmount,
  totalPayment: quoteItemValue?.totalPayment,
  vat: quoteItemValue?.vat,
  totalVAT: quoteItemValue?.totalVAT,
  vatStatus: quoteItemValue?.vatStatus,
  roundPrices: quoteItemValue?.client?.roundPrices,
  documentItems: mapDocumentItemsForDraftTax(quoteItemValue?.documentItems),
  ...buildDraftTaxContext(quoteItemValue),
  ...overrides,
});

export const mergeDraftTaxCalculationResult = (
  quoteItemValue: any,
  calculatedData: any,
) => ({
  ...quoteItemValue,
  discount: calculatedData?.discount,
  discountAmount: calculatedData?.discountAmount,
  totalPayment: calculatedData?.totalPayment,
  totalPrice: calculatedData?.totalPrice,
  totalPriceAfterDiscount: calculatedData?.totalPriceAfterDiscount,
  totalVAT: calculatedData?.totalVAT,
  vat: calculatedData?.vat,
  taxComposition: calculatedData?.taxComposition ?? null,
  taxCalculating: false,
});

export const mergeDraftTaxDocumentCalculationResult = (
  quoteItemValue: any,
  documentData: any,
  documentItems?: any[],
) => ({
  ...quoteItemValue,
  discount: documentData?.discount,
  discountAmount: documentData?.discountAmount,
  totalPayment: documentData?.totalPayment,
  totalPrice: documentData?.totalPrice,
  totalPriceAfterDiscount: documentData?.totalPriceAfterDiscount,
  totalVAT: documentData?.totalVAT,
  vat: documentData?.vat,
  taxComposition: documentData?.taxComposition ?? null,
  taxCalculating: false,
  ...(documentItems ? { documentItems } : {}),
});

export const applyOptimisticDraftInvoiceTax = (
  quoteItemValue: any,
  erpType?: number,
) => applyOptimisticTaxCalculating(quoteItemValue, erpType) ?? quoteItemValue;
